import { Navigate } from 'react-router-dom'

import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { readPendingClaimPath } from '../../_data/pending-claim'
import { isEmbeddedExperience } from '../../_utilities/embed-context'
import { RouteGuardProps } from './types'

/**
 * Wraps a route to enforce auth + onboarding state.
 *
 * - `kind="protected"`: requires sign-in. Used for /onboarding (renter
 *   should be signed in but not yet onboarded).
 * - `kind="onboarded-only"`: requires sign-in AND a finished onboarding
 *   flow. Used for /dashboard, /profile, /saved-listings. If signed in
 *   but onboarding incomplete, redirects to /onboarding.
 * - `kind="public-only"`: requires NOT being signed in. Used for
 *   /sign-in. Signed-in users are bounced to `/`, where AuthGate routes
 *   them to the correct land (resident → /my-home, search → /dashboard).
 *
 * While auth or profile is still resolving, renders nothing — a brief
 * blank frame is preferable to a flash of the wrong content followed by a
 * redirect.
 *
 * `hideWhenEmbedded` bounces the route to /dashboard when the app is running
 * inside a host-site embed (used for /discover, which should not appear in a
 * partner's page). Direct links and stale in-app navigation both land safely.
 *
 * @param {RouteGuardProps} props
 * @returns {JSX.Element | null}
 */
const RouteGuard = ({
  kind,
  children,
  hideWhenEmbedded = false,
}: RouteGuardProps): JSX.Element | null => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { isProfileLoading, hasFinishedOnboarding } = useQuickPassProfile()

  if (isAuthLoading === true) {
    return null
  }

  if (kind === 'public-only') {
    if (isAuthenticated === true) {
      const pendingClaimPath = readPendingClaimPath()
      if (pendingClaimPath !== null) {
        return <Navigate to={pendingClaimPath} replace />
      }

      return <Navigate to="/" replace />
    }

    return <>{children}</>
  }

  if (isAuthenticated === false) {
    return <Navigate to="/sign-in" replace />
  }

  if (hideWhenEmbedded === true && isEmbeddedExperience() === true) {
    return <Navigate to="/dashboard" replace />
  }

  if (kind === 'protected') {
    return <>{children}</>
  }

  if (isProfileLoading === true) {
    return null
  }

  if (hasFinishedOnboarding === false) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

export default RouteGuard
